import { ApolloClient, DocumentNode, ObservableQuery } from '@apollo/client';
import { addSeconds, differenceInSeconds, startOfToday } from 'date-fns';
import {
  CREATED_ASSET_STATE,
  CREATED_SITE_STATE,
  FETCH_MORE_ASSET_OPERATIONS_STATES,
  FETCH_MORE_SITE_OPERATIONS_STATES,
  GET_ASSET_OPERATIONS_STATES,
  GET_ASSET_OPERATIONS_SUMMARY,
  GET_SITE_OPERATIONS_STATES,
  GET_SITE_OPERATIONS_SUMMARY,
} from 'pages/Operations/queries';

const secondsInHour = 3600;

type AssetQueryVariables = {
  assetId: number;
  start: Date;
  end: Date;
};

type SiteQueryVariables = {
  siteId: number;
  start: Date;
  end: Date;
};

type FetchDataGapAndSubscribeBaseArgs = {
  apolloClient: ApolloClient;
  queryVariables: AssetQueryVariables | SiteQueryVariables;
  fetchMore: ObservableQuery['fetchMore'];
  subscribeToMore: ObservableQuery['subscribeToMore'];
  nowMode: boolean;
};
interface FetchDataGapAndSubscribeArgs extends FetchDataGapAndSubscribeBaseArgs {
  fetchArgs: {
    getStatesQueryDocument: DocumentNode;
    fetchMoreStatesQueryDocument: DocumentNode;
  };
  subscribeArgs: {
    createStateQueryDocument: DocumentNode;
    getStatesSymmaryQueryDocument: DocumentNode;
    subscriptionKey: string;
  };
}

type FetchMoreStatesDataArgs = Omit<
  FetchDataGapAndSubscribeArgs,
  'subscribeToMore'
>;

type SubscribeToMoreStatesDataArgs = Omit<
  FetchDataGapAndSubscribeArgs,
  'fetchMore'
>;

interface FetchAssetStatesDataGapAndSubscribeArgs extends FetchDataGapAndSubscribeBaseArgs {
  queryVariables: AssetQueryVariables;
}

interface FetchSiteStatesDataGapAndSubscribeArgs extends FetchDataGapAndSubscribeBaseArgs {
  queryVariables: SiteQueryVariables;
}

type RecalculateStateSummaryArgs = {
  statesData: any; // NOSONAR
  state: any; // NOSONAR
};

type SiteAndAssetState = {
  frequency: number;
  id: string;
  instruction: number;
  powerOutput: number;
  stateOfCharge: number;
  temperature?: number;
  time: string;
  voltage?: number;
};

export const getLatestStateTime = (states: SiteAndAssetState[]) => {
  if (!states?.length) {
    return new Date();
  }

  const latestStateTime = states.reduce(
    (max: string, state: SiteAndAssetState) =>
      state.time > max ? state.time : max,
    startOfToday().toISOString(),
  );

  return new Date(latestStateTime);
};

export const getFirstStateTime = (states: SiteAndAssetState[]) => {
  if (!states?.length) {
    return new Date();
  }

  const firstStateTime = states.reduce((prev, curr) => {
    const prevTime = prev.time;
    const currTime = curr.time;
    return prevTime < currTime ? prev : curr;
  }, states[0])?.time;

  return new Date(firstStateTime ?? '');
};

export const addStateSum = ({
  statesData,
  state,
}: RecalculateStateSummaryArgs) => {
  const { MWhDelta, cyclesDelta } = calculateStateSummaryDelta(
    statesData?.installedCapacity,
    state,
    getLatestStateTime(statesData?.states),
  );

  return {
    stateSummary: {
      import: statesData?.stateSummary?.import + Math.min(0, MWhDelta),
      export: statesData?.stateSummary?.export + Math.max(0, MWhDelta),
      cycles: statesData?.stateSummary?.cycles + Math.max(0, cyclesDelta),
      __typename: 'StateSummary',
    },
  };
};

export const reduceStateSum = ({
  statesData,
  state,
}: RecalculateStateSummaryArgs) => {
  const { MWhDelta, cyclesDelta } = calculateStateSummaryDelta(
    statesData?.installedCapacity,
    state,
    getFirstStateTime(statesData?.states),
  );

  return {
    stateSummary: {
      import: statesData?.stateSummary?.import - Math.min(0, MWhDelta),
      export: statesData?.stateSummary?.export - Math.max(0, MWhDelta),
      cycles: statesData?.stateSummary?.cycles - Math.max(0, cyclesDelta),
      __typename: 'StateSummary',
    },
  };
};

const calculateStateSummaryDelta = (
  installedCapacity: number,
  state: any,
  nearestStateTime: Date,
) => {
  const subscriptionTimeDelta = Math.abs(
    differenceInSeconds(new Date(state.time), nearestStateTime),
  );

  const getMWhDelta = (powerOutput = 0) =>
    (powerOutput * subscriptionTimeDelta) / secondsInHour;

  const getCyclesDelta = (MWhDelta: number) =>
    (Math.abs(MWhDelta / 1000) * 0.5) / installedCapacity;

  const MWhDelta = getMWhDelta(state.powerOutput);
  const cyclesDelta = getCyclesDelta(MWhDelta);

  return { MWhDelta, cyclesDelta };
};

export const fetchAssetStatesDataGapAndSubscribe = async (
  args: FetchAssetStatesDataGapAndSubscribeArgs,
) => {
  if (!args.queryVariables.assetId) {
    return () => {};
  }

  return await fetchStatesDataGapAndSubscribe({
    ...args,
    fetchArgs: {
      fetchMoreStatesQueryDocument: FETCH_MORE_ASSET_OPERATIONS_STATES,
      getStatesQueryDocument: GET_ASSET_OPERATIONS_STATES,
    },
    subscribeArgs: {
      createStateQueryDocument: CREATED_ASSET_STATE,
      getStatesSymmaryQueryDocument: GET_ASSET_OPERATIONS_SUMMARY,
      subscriptionKey: 'createdAssetState',
    },
  });
};

export const fetchSiteStatesDataGapAndSubscribe = async (
  args: FetchSiteStatesDataGapAndSubscribeArgs,
) => {
  if (!args.queryVariables.siteId) {
    return () => {};
  }

  return await fetchStatesDataGapAndSubscribe({
    ...args,
    fetchArgs: {
      fetchMoreStatesQueryDocument: FETCH_MORE_SITE_OPERATIONS_STATES,
      getStatesQueryDocument: GET_SITE_OPERATIONS_STATES,
    },
    subscribeArgs: {
      createStateQueryDocument: CREATED_SITE_STATE,
      getStatesSymmaryQueryDocument: GET_SITE_OPERATIONS_SUMMARY,
      subscriptionKey: 'createdSiteState',
    },
  });
};

const fetchStatesDataGapAndSubscribe = async (
  args: FetchDataGapAndSubscribeArgs,
) => {
  let onUnsubscribe = () => {};

  if (
    (args.queryVariables.end > new Date() &&
      args.queryVariables.start <= new Date()) ||
    args.nowMode
  ) {
    await fetchMoreStatesData(args).then(
      async () => (onUnsubscribe = await subscribeToMoreStatesData(args)),
    );
  }

  return onUnsubscribe;
};

// The code below is tightly coupled and dependant on the statePolicy in
// src/pluto-graphql/apollo/client
// See DOCS/graphql-apollo.md for more info

const fetchMoreStatesData = async ({
  apolloClient,
  queryVariables: variables,
  fetchArgs,
  fetchMore,
  nowMode,
}: FetchMoreStatesDataArgs) => {
  const cachedData = apolloClient.readQuery({
    query: fetchArgs.getStatesQueryDocument,
    variables,
  });

  if (!nowMode && !!cachedData) {
    const parentType = getParentType(cachedData);
    const latestTime = getLatestStateTime(
      (cachedData as any)[parentType]?.states,
    );
    variables = {
      ...variables,
      start: addSeconds(new Date(latestTime), 1),
    };

    await fetchMore({
      query: fetchArgs.fetchMoreStatesQueryDocument,
      variables,
      updateQuery: (queryData: any, { fetchMoreResult }: any) =>
        updateStatesQueryData(
          parentType,
          queryData,
          fetchMoreResult?.[parentType]?.states,
        ),
    });
  }
};

const subscribeToMoreStatesData = async ({
  apolloClient,
  queryVariables: variables,
  subscribeArgs,
  subscribeToMore,
  nowMode,
}: SubscribeToMoreStatesDataArgs) => {
  return subscribeToMore({
    document: subscribeArgs.createStateQueryDocument,
    variables,
    updateQuery: (queryData, { subscriptionData }) => {
      const newState = (subscriptionData?.data as any)?.[
        subscribeArgs.subscriptionKey
      ];

      const updatedQueryData = handleNewStateEvent(
        apolloClient,
        queryData,
        newState,
        variables,
        nowMode,
        subscribeArgs.getStatesSymmaryQueryDocument,
      );

      return updatedQueryData;
    },
  });
};

export function handleNewStateEvent(
  apolloClient: ApolloClient,
  queryData: any,
  newState: any,
  variables: SiteQueryVariables | AssetQueryVariables,
  nowMode: boolean,
  query: DocumentNode,
) {
  const parentType = getParentType(queryData);
  if (!newState || !queryData || !queryData[parentType]) {
    return;
  }

  const updatedStatesQueryData = updateStatesQueryData(parentType, queryData, [
    newState,
  ]);

  const poppedState = nowMode
    ? (() => {
        const timeWindowMs = +variables.end - +variables.start;

        const cutoffDateAsNumber = +new Date(newState.time) - timeWindowMs;

        const firstState = updatedStatesQueryData[parentType].states[0];

        if (+new Date(firstState.time) >= cutoffDateAsNumber) {
          return undefined;
        }

        return updatedStatesQueryData[parentType].states.shift();
      })()
    : undefined;

  updateStatesSummary(
    apolloClient,
    query,
    variables,
    parentType,
    updatedStatesQueryData,
    newState,
    poppedState,
  );

  return updatedStatesQueryData;
}

export function updateStatesQueryData(
  parentType: string,
  queryData: any,
  newStates: any[],
): any {
  if (!newStates) {
    return queryData;
  }

  const updatedQueryData = {
    [parentType]: {
      ...queryData[parentType],
      states: [...(queryData[parentType]?.states || []), ...(newStates || [])],
    },
  };

  return updatedQueryData;
}

const getParentType = (queryDataObject: any) =>
  (
    queryDataObject?.site?.__typename ??
    queryDataObject?.asset?.__typename ??
    ''
  ).toLowerCase();

function updateStatesSummary(
  apolloClient: ApolloClient,
  query: DocumentNode,
  variables: AssetQueryVariables | SiteQueryVariables,
  parentType: string,
  updatedStatesQueryData: any,
  newState: any,
  poppedState: any,
) {
  const cachedSummaryData = apolloClient.readQuery({
    query,
    variables,
  });

  const isCachedStatesSummaryAvailable =
    (cachedSummaryData as any)?.[parentType] !== undefined;

  if (isCachedStatesSummaryAvailable) {
    const statesData = {
      ...updatedStatesQueryData[parentType],
      ...(cachedSummaryData as any)[parentType],
    };

    let updatedSummaryData = addStateSum({
      statesData,
      state: newState,
    });

    if (poppedState) {
      updatedSummaryData = reduceStateSum({
        statesData,
        state: poppedState,
      });
    }

    apolloClient.writeQuery({
      query,
      data: {
        ...(cachedSummaryData as any),
        [parentType]: {
          ...(cachedSummaryData as any)[parentType],
          ...updatedSummaryData,
        },
      },
      variables,
    });
  }
}
